define('BackorderSummary.View', [
  'uc_backorder_summary.tpl',
  'PageType.Base.View',
  'BackorderSummary.Model',
  'Utils',
  'underscore',
  'jQuery'
], function (
  // eslint-disable-next-line camelcase
  uc_backorder_summary_tpl,
  PageType,
  BackorderSummaryModel,
  Utils,
  _,
  jQuery
) {
  'use strict';

  // Quantities are summed across lines (here and in the backend). Floating
  // point summation leaks artifacts like 12.000000000000002, so round the
  // displayed quantity to at most 2 decimals. Number(...toFixed(2)) strips
  // trailing zeros, keeping whole quantities as integers (12, not 12.00).
  function roundQuantity(value) {
    var n = Number(value) || 0;
    return Number(n.toFixed(2));
  }

  return PageType.PageTypeBaseView.extend({

    // eslint-disable-next-line camelcase
    template: uc_backorder_summary_tpl,

    title: Utils.translate('Backorder Summary'),

    attributes: {
      'id': 'backorder-summary',
      'class': 'backorder-summary'
    },
    
    initialize: function initialize(options) {
      this.options = options || {};
      this.model = new BackorderSummaryModel();
      this.isLoading = true;
      this.loadError = null;
      this.lines = [];
      this.isLoadingMore = false;
      this.totalLines = 0;
    },

    beforeShowContent: function beforeShowContent() {
      var self = this;
      var deferred = jQuery.Deferred();

      this.events = {
        'click [data-action="backorder-export"]': 'exportLines'
      };

      this.getBreadcrumbPages = function () {
        return [
          {
            text: Utils.translate('Backorder Summary'),
            href: '/backorder-summary'
          }
        ];
      };

      this.fetchPage(0).done(function (data) {
        self.isLoading = false;
        self.lines = data.lines || [];
        self.totalLines = data.totalLines || self.lines.length;
        self.isLoadingMore = Boolean(data.hasMore);
        deferred.resolve();

        if (data.hasMore) {
          self.loadRemainingPages(1);
        }
      }).fail(function (message) {
        self.isLoading = false;
        self.loadError = message;
        deferred.resolve();
      });

      return deferred;
    },

    fetchPage: function fetchPage(page) {
      var deferred = jQuery.Deferred();

      jQuery.ajax({
        url: this.model.pageUrl(page),
        type: 'GET',
        dataType: 'json'
      }).done(function (response) {
        if (response && response.code === 200 && response.data) {
          deferred.resolve(response.data);
          return;
        }
        deferred.reject(
          response && response.message
            || Utils.translate('Unable to load backorder data. Please try again later.')
        );
      }).fail(function (jqXhr) {
        deferred.reject(
          jqXhr && jqXhr.responseJSON && jqXhr.responseJSON.message
            || Utils.translate('Unable to load backorder data. Please try again later.')
        );
      });

      return deferred;
    },

    loadRemainingPages: function loadRemainingPages(page) {
      var self = this;

      this.fetchPage(page).done(function (data) {
        self.lines = self.lines.concat(data.lines || []);

        if (data.hasMore) {
          self.render();
          self.loadRemainingPages(page + 1);
          return;
        }

        self.isLoadingMore = false;
        self.render();
      }).fail(function () {
        self.isLoadingMore = false;
        self.loadError = Utils.translate(
          'Some lines could not be loaded. The totals below cover only the lines shown.'
        );
        self.render();
      });
    },

    computeTotals: function computeTotals(lines) {
      var totals = {
        lineCount: lines.length,
        quantityOrdered: 0,
        quantityShipped: 0,
        quantityBackordered: 0,
        extendedPrice: 0
      };

      _.each(lines, function (line) {
        totals.quantityOrdered += line.quantityOrdered || 0;
        totals.quantityShipped += line.quantityShipped || 0;
        totals.quantityBackordered += line.quantityBackordered || 0;
        totals.extendedPrice += line.extendedPrice || 0;
      });

      return totals;
    },

    groupLinesByOrder: function groupLinesByOrder(lines) {
      var groups = _.groupBy(lines || [], 'soInternalId');

      return _.map(groups, function (orderLines) {
        var head = orderLines[0] || {};
        var orderExtended = 0;
        var orderOrdered = 0;
        var orderShipped = 0;
        var orderBackordered = 0;

        var formattedLines = _.map(orderLines, function (line) {
          orderExtended += line.extendedPrice || 0;
          orderOrdered += line.quantityOrdered || 0;
          orderShipped += line.quantityShipped || 0;
          orderBackordered += line.quantityBackordered || 0;

          return _.extend({}, line, {
            unitPriceFormatted: Utils.formatCurrency(line.unitPrice || 0),
            extendedPriceFormatted: Utils.formatCurrency(line.extendedPrice || 0)
          });
        });

        return {
          soInternalId: head.soInternalId,
          soNumber: head.soNumber,
          soDate: head.soDate,
          soStatus: head.soStatus,
          soStatusCode: head.soStatusCode,
          lines: formattedLines,
          subtotal: {
            lineCount: formattedLines.length,
            quantityOrdered: roundQuantity(orderOrdered),
            quantityShipped: roundQuantity(orderShipped),
            quantityBackordered: roundQuantity(orderBackordered),
            extendedPrice: orderExtended,
            extendedPriceFormatted: Utils.formatCurrency(orderExtended)
          }
        };
      });
    },

    // The service exports every open line itself, so the buttons only need
    // the first page to know there is something to export, and stay off
    // when loading failed.
    canExport: function canExport() {
      return !this.isLoading && !this.loadError && this.lines.length > 0;
    },

    exportLines: function exportLines(e) {
      var action = jQuery(e.currentTarget).data('export');

      e.preventDefault();

      if (!action || !this.canExport()) {
        return;
      }

      // The service answers with a file attachment. A new tab matches the
      // PLP export and keeps a plain-text error off this page.
      window.open(this.model.exportUrl(action), '_blank');
    },

    getContext: function getContext() {
      var lines = this.lines || [];
      var totals = this.computeTotals(lines);

      var orders = this.groupLinesByOrder(lines);

      // Backend already sorts the underlying search by trandate DESC, but
      // _.groupBy returns an object keyed by numeric soInternalId; iteration
      // then yields keys in ascending numeric order, surfacing the oldest
      // orders first. Re-sort explicitly to preserve newest-first.
      orders.sort(function (a, b) {
        if (a.soDate === b.soDate) {
          return (Number(b.soInternalId) || 0) - (Number(a.soInternalId) || 0);
        }
        return a.soDate < b.soDate ? 1 : -1;
      });

      return {
        pageTitle: Utils.translate('Backorder Summary'),
        isLoading: this.isLoading,
        hasError: Boolean(this.loadError),
        errorMessage: this.loadError || '',
        hasOrders: orders.length > 0,
        canExport: this.canExport(),
        orders: orders,
        isLoadingMore: this.isLoadingMore,
        loadedLineCount: lines.length,
        totalLineCount: this.totalLines,
        totals: _.extend({}, totals, {
          quantityOrdered: roundQuantity(totals.quantityOrdered),
          quantityShipped: roundQuantity(totals.quantityShipped),
          quantityBackordered: roundQuantity(totals.quantityBackordered),
          extendedPriceFormatted: Utils.formatCurrency(totals.extendedPrice || 0)
        })
      };
    }
  });
});
